import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["Button", "useContentContainer", "onInsertComplete", "resetSelectedType", "allowSenderMeetingLink", "onInsertContent", "onOpenPopover"];
import { useCallback, useContext, useState, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { RTEToolbarButton } from '../../components/StyledTextToolbarButton';
import { TextToolbarIcon } from 'rich-text-lib/components/editor/TextToolbarIcon';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import useDebouncedEditorStateContext from '../../contexts/useDebouncedEditorStateContext';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { Fragment, Slice } from 'hubspot-prosemirror/pm/model';
import { FocusPlugin } from '../../plugins/FocusPlugin';
import MeetingsPopover from './MeetingsPopover';
import { MeetingsIcon } from 'foundations-assets/icons/components/MeetingsIcon';
import { EditorConfigContext } from '../../contexts/EditorConfigContext';
import { getGlobalVars } from '../../utils/globalVarUtil';
import { convertToPlaintextState } from '../../utils/plaintextUtils';

/** Configuration for the Meetings insert capability (scheduling links) */
import { jsx as _jsx } from "react/jsx-runtime";
const MeetingsButton = ({
  onClick
}) => /*#__PURE__*/_jsx(Tooltip, {
  title: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "hubspot-prosemirror.tooltips.insertMeeting"
  }),
  children: /*#__PURE__*/_jsx(RTEToolbarButton, {
    "data-test-id": "meetings",
    onClick: onClick,
    selected: false,
    children: /*#__PURE__*/_jsx(TextToolbarIcon, {
      icon: MeetingsIcon
    })
  })
});
export default function InsertMeetings(_ref) {
  let {
      Button = MeetingsButton,
      useContentContainer,
      onInsertComplete = () => {},
      resetSelectedType = () => {},
      allowSenderMeetingLink,
      onInsertContent = () => {},
      onOpenPopover = () => {}
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const editorConfig = useContext(EditorConfigContext);
  const {
    usePlaintextMode,
    plaintextOptions
  } = getGlobalVars(editorConfig);
  const [open, setOpen] = useState(false);
  const insertMeeting = useCallback(({
    meetingURL,
    meetingName
  }) => {
    const meetingsNode = editorState.schema.nodes.meetings.create({
      meetingURL,
      meetingName
    }, null, editorState.selection.$from.marks());
    const tr = editorState.tr.replaceSelection(new Slice(Fragment.fromArray([meetingsNode]), 0, 0));
    tr.setMeta(FocusPlugin, {
      focusView: true,
      focusViewPos: tr.mapping.map(editorState.selection.to)
    });
    const newState = editorState.apply(tr);
    setEditorState(usePlaintextMode ? convertToPlaintextState(newState, plaintextOptions) : newState);
    onInsertComplete();
    onInsertContent();
  }, [editorState, setEditorState, usePlaintextMode, plaintextOptions, onInsertComplete, onInsertContent]);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  const onOpenChange = useCallback(e => {
    setOpen(e.target.value);
  }, [setOpen]);
  const MeetingsPopoverContainer = useMemo(() => {
    if (useContentContainer) {
      return /*#__PURE__*/_jsx(MeetingsPopover, Object.assign({}, props, {
        onChange: insertMeeting,
        onClose: resetSelectedType,
        allowSenderMeetingLink: allowSenderMeetingLink,
        isNew: true
      }));
    }
    return /*#__PURE__*/_jsx(Popover, {
      open: open,
      onOpenChange: onOpenChange,
      content: /*#__PURE__*/_jsx(MeetingsPopover, Object.assign({}, props, {
        onChange: insertMeeting,
        onClose: handleClose,
        isNew: true,
        allowSenderMeetingLink: allowSenderMeetingLink
      })),
      children: /*#__PURE__*/_jsx(Button, {
        onClick: () => {
          if (!open) {
            onOpenPopover();
          }
          setOpen(!open);
        }
      })
    });
  }, [Button, handleClose, insertMeeting, onOpenChange, onOpenPopover, open, props, resetSelectedType, useContentContainer, allowSenderMeetingLink]);
  return MeetingsPopoverContainer;
}